import { h } from "@stencil/core";
/**
 * The `ch-grid-virtual-scroller` component that displays a subset of items.
 * It optimizes the rendering of large data sets by only rendering the items that are currently visible on the screen
 * based on the viewport size and scroll position.
 */
export class ChGridVirtualScroller {
    constructor() {
        this.headerHeight = 0;
        this.rowHeight = 0;
        this.browserHeight = document.documentElement.clientHeight;
        this.hasGridScroll = false;
        this.hasWindowScroll = false;
        this.maxViewPortItems = 1;
        this.items = undefined;
        this.itemsCount = undefined;
        this.viewPortItems = undefined;
    }
    gridMainEl;
    resizeObserver = new ResizeObserver(this.resizeHandler.bind(this));
    virtualRowSizeElement;
    el;
    headerHeightHandler() {
        if (this.gridMainEl) {
            this.updateViewPortItems();
        }
    }
    rowHeightHandler() {
        if (this.gridMainEl) {
            this.updateViewPortItems();
        }
    }
    hasScrollHandler() {
        if (this.hasGridScroll) {
            this.gridMainEl.addEventListener("scroll", this.updateViewPortItems.bind(this), { passive: true });
        }
        else {
            this.gridMainEl.removeEventListener("scroll", this.updateViewPortItems.bind(this));
        }
        this.updateViewPortItems();
    }
    hasWindowScrollHandler() {
        if (this.hasWindowScroll) {
            document.addEventListener("scroll", this.updateViewPortItems.bind(this), {
                passive: true
            });
        }
        else {
            document.removeEventListener("scroll", this.updateViewPortItems.bind(this));
        }
        this.updateViewPortItems();
    }
    maxViewPortItemsHandler() {
        if (this.gridMainEl) {
            this.updateViewPortItems();
        }
    }
    itemsHandler() {
        if (this.gridMainEl) {
            this.updateViewPortItems();
        }
    }
    itemsCountHandler() {
        if (this.gridMainEl) {
            this.updateViewPortItems();
        }
    }
    /**
     *Event emitted when the list of visible items in the grid changes.
     */
    viewPortItemsChanged;
    componentWillLoad() {
        this.gridMainEl = this.el.assignedSlot.parentElement;
        this.resizeObserver.observe(this.gridMainEl);
        this.resizeObserver.observe(document.documentElement);
    }
    componentDidLoad() {
        this.resizeObserver.observe(this.virtualRowSizeElement);
        this.updateViewPortItems();
    }
    disconnectedCallback() {
        this.resizeObserver.disconnect();
    }
    resizeHandler() {
        const rowHeights = getComputedStyle(this.gridMainEl).gridTemplateRows.split(" ");
        this.browserHeight = document.documentElement.clientHeight;
        // [0]:header height, [1]:deprecated, [2]:first row height
        this.headerHeight = rowHeights.length >= 3 ? parseInt(rowHeights[0]) : 0;
        this.rowHeight = rowHeights.length >= 3 ? parseInt(rowHeights[2]) : 0;
        if (this.rowHeight > 0) {
            this.hasGridScroll =
                this.gridMainEl.scrollHeight !== this.gridMainEl.clientHeight;
            this.hasWindowScroll =
                !this.hasGridScroll &&
                    this.gridMainEl.clientHeight > this.browserHeight;
            this.maxViewPortItems = Math.ceil(this.browserHeight / this.rowHeight);
        }
    }
    updateViewPortItems() {
        const percentScroll = this.getPercentScroll();
        let startIndex;
        if (percentScroll <= 50) {
            startIndex = Math.floor((percentScroll *
                Math.max(this.items.length - this.maxViewPortItems, 0)) /
                100);
        }
        else {
            startIndex = Math.ceil((percentScroll *
                Math.max(this.items.length - this.maxViewPortItems, 0)) /
                100);
        }
        const hiddenHeight = this.maxViewPortItems * this.rowHeight +
            this.headerHeight -
            this.gridMainEl.clientHeight;
        const offset = (percentScroll * hiddenHeight) / 100;
        this.el.style.setProperty("--ch-grid-virtual-scroller-position", `translateY(${this.gridMainEl.scrollTop - offset}px)`);
        this.el.style.setProperty("--ch-grid-virtual-scroller-height", `${(this.items.length - this.maxViewPortItems) * this.rowHeight}px`);
        this.viewPortItems = this.items.slice(startIndex, startIndex + this.maxViewPortItems);
        this.viewPortItemsChanged.emit();
    }
    getPercentScroll() {
        let hiddenHeight = 0;
        let scrollPosition = 0;
        if (this.hasGridScroll) {
            hiddenHeight =
                this.gridMainEl.scrollHeight - this.gridMainEl.clientHeight;
            scrollPosition = this.gridMainEl.scrollTop;
        }
        else if (this.hasWindowScroll) {
            const gridMainRect = this.gridMainEl.getBoundingClientRect();
            hiddenHeight = this.gridMainEl.clientHeight - this.browserHeight;
            scrollPosition = Math.min(gridMainRect.top >= 0 ? 0 : gridMainRect.top * -1, hiddenHeight);
        }
        return hiddenHeight > 0 ? (scrollPosition * 100) / hiddenHeight : 0;
    }
    render() {
        return (h("div", { ref: el => (this.virtualRowSizeElement = el), class: "virtual-scroller-row-size" }));
    }
    static get is() { return "ch-grid-virtual-scroller"; }
    static get originalStyleUrls() {
        return {
            "$": ["ch-grid-virtual-scroller.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ch-grid-virtual-scroller.css"]
        };
    }
    static get properties() {
        return {
            "items": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "any[]",
                    "resolved": "any[]",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The list of items to be rendered in the grid."
                }
            },
            "itemsCount": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The number of elements in the items list.\nUse if the list changes, without recreating the array."
                },
                "attribute": "items-count",
                "reflect": false
            },
            "viewPortItems": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "any[]",
                    "resolved": "any[]",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The list of items to display within the current viewport."
                }
            }
        };
    }
    static get states() {
        return {
            "headerHeight": {},
            "rowHeight": {},
            "browserHeight": {},
            "hasGridScroll": {},
            "hasWindowScroll": {},
            "maxViewPortItems": {}
        };
    }
    static get events() {
        return [{
                "method": "viewPortItemsChanged",
                "name": "viewPortItemsChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the list of visible items in the grid changes."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "headerHeight",
                "methodName": "headerHeightHandler"
            }, {
                "propName": "rowHeight",
                "methodName": "rowHeightHandler"
            }, {
                "propName": "hasGridScroll",
                "methodName": "hasScrollHandler"
            }, {
                "propName": "hasWindowScroll",
                "methodName": "hasWindowScrollHandler"
            }, {
                "propName": "maxViewPortItems",
                "methodName": "maxViewPortItemsHandler"
            }, {
                "propName": "items",
                "methodName": "itemsHandler"
            }, {
                "propName": "itemsCount",
                "methodName": "itemsCountHandler"
            }];
    }
}
//# sourceMappingURL=ch-grid-virtual-scroller.js.map
